import React, { useState, useEffect } from 'react';
import { ref, onValue, update, remove } from 'firebase/database';
import { database } from '../../firebase/config';
import { 
  FaCheck, 
  FaTimes, 
  FaEdit, 
  FaTrash, 
  FaSearch,
  FaFilter,
  FaCalendarAlt,
  FaUser,
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaEye,
  FaSpinner
} from 'react-icons/fa';
import './Admin.css';

const GestionRendezVous = () => {
  const [rendezvous, setRendezvous] = useState([]);
  const [filteredRdv, setFilteredRdv] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatut, setFilterStatut] = useState('tous');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedRdv, setSelectedRdv] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    setLoading(true);
    const rendezvousRef = ref(database, 'rendezvous');
    const unsubscribe = onValue(rendezvousRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const rdvList = [];
        Object.entries(data).forEach(([userId, userRdv]) => {
          Object.entries(userRdv).forEach(([rdvId, rdv]) => {
            rdvList.push({
              id: rdvId,
              userId,
              ...rdv,
              dateObj: new Date(rdv.date)
            });
          });
        });
        
        rdvList.sort((a, b) => b.dateObj - a.dateObj);
        setRendezvous(rdvList);
        setFilteredRdv(rdvList);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    let filtered = rendezvous;
    
    if (searchTerm) {
      filtered = filtered.filter(rdv => 
        rdv.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        rdv.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        rdv.telephone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        rdv.adresseDepart?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        rdv.adresseArrivee?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (filterStatut !== 'tous') {
      filtered = filtered.filter(rdv => rdv.statut === filterStatut);
    }
    
    setFilteredRdv(filtered);
  }, [searchTerm, filterStatut, rendezvous]);

  const handleStatutChange = async (rdvId, userId, nouveauStatut) => {
    try {
      const rdvRef = ref(database, `rendezvous/${userId}/${rdvId}`);
      await update(rdvRef, { statut: nouveauStatut });
    } catch (error) {
      console.error('Erreur lors du changement de statut', error);
    }
  };

  const handleDelete = async (rdvId, userId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce rendez-vous ? Cette action est irréversible.')) {
      try {
        const rdvRef = ref(database, `rendezvous/${userId}/${rdvId}`);
        await remove(rdvRef);
      } catch (error) {
        console.error('Erreur lors de la suppression', error);
        alert('Erreur lors de la suppression');
      }
    }
  };

  const handleEdit = (rdv) => {
    setEditingId(rdv.id);
    setEditForm({ ...rdv });
  };

  const handleSaveEdit = async () => {
    try {
      const rdvRef = ref(database, `rendezvous/${editForm.userId}/${editForm.id}`);
      await update(rdvRef, {
        nom: editForm.nom,
        email: editForm.email,
        telephone: editForm.telephone,
        date: editForm.date,
        adresseDepart: editForm.adresseDepart,
        adresseArrivee: editForm.adresseArrivee,
        typeService: editForm.typeService,
        statut: editForm.statut,
        message: editForm.message
      });
      setEditingId(null);
    } catch (error) {
      console.error('Erreur lors de la modification', error);
      alert('Erreur lors de la modification');
    }
  };

  const viewDetails = (rdv) => {
    setSelectedRdv(rdv);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedRdv(null);
  };

  const getStatutClass = (statut) => {
    switch(statut) {
      case 'en attente': return 'statut-badge pending';
      case 'confirmé': return 'statut-badge confirmed';
      case 'terminé': return 'statut-badge completed';
      default: return 'statut-badge';
    }
  };

  const getStatutText = (statut) => {
    switch(statut) {
      case 'en attente': return 'En attente';
      case 'confirmé': return 'Confirmé';
      case 'terminé': return 'Terminé';
      default: return statut;
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <FaSpinner className="spinner" />
        <p>Chargement des rendez-vous...</p>
      </div>
    );
  }

  return (
    <div className="gestion-container">
      <div className="page-header">
        <div>
          <h2>Gestion des rendez-vous</h2>
          <p className="page-subtitle">Consultez et gérez tous les rendez-vous clients</p>
        </div>
        <div className="stats-summary">
          <div className="stat-badge total">
            <span className="stat-number">{rendezvous.length}</span>
            <span className="stat-label">Total</span>
          </div>
          <div className="stat-badge pending">
            <span className="stat-number">{rendezvous.filter(r => r.statut === 'en attente').length}</span>
            <span className="stat-label">En attente</span>
          </div>
          <div className="stat-badge confirmed">
            <span className="stat-number">{rendezvous.filter(r => r.statut === 'confirmé').length}</span>
            <span className="stat-label">Confirmés</span>
          </div>
        </div>
      </div>
      
      <div className="filters-section">
        <div className="search-box">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Rechercher par nom, email, téléphone ou adresse..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="clear-search" onClick={() => setSearchTerm('')}>
              <FaTimes />
            </button>
          )}
        </div>
        
        <div className="filter-group">
          <FaFilter className="filter-icon" />
          <select 
            value={filterStatut} 
            onChange={(e) => setFilterStatut(e.target.value)}
            className="filter-select"
          >
            <option value="tous">Tous les statuts</option>
            <option value="en attente">En attente</option>
            <option value="confirmé">Confirmé</option>
            <option value="terminé">Terminé</option>
          </select>
        </div>
      </div>

      {filteredRdv.length === 0 ? (
        <div className="no-data">
          <FaCalendarAlt className="no-data-icon" />
          <p>Aucun rendez-vous trouvé</p>
          <span>Essayez de modifier vos critères de recherche</span>
        </div>
      ) : (
        <div className="table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Date</th>
                <th>Adresses</th>
                <th>Service</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRdv.map((rdv) => (
                <tr key={rdv.id} className={editingId === rdv.id ? 'editing-row' : ''}>
                  {editingId === rdv.id ? (
                    <>
                      <td>
                        <input
                          type="text"
                          value={editForm.nom || ''}
                          onChange={(e) => setEditForm({...editForm, nom: e.target.value})}
                          placeholder="Nom"
                          className="edit-input"
                        />
                        <input
                          type="email"
                          value={editForm.email || ''}
                          onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                          placeholder="Email"
                          className="edit-input"
                        />
                        <input
                          type="tel"
                          value={editForm.telephone || ''}
                          onChange={(e) => setEditForm({...editForm, telephone: e.target.value})}
                          placeholder="Téléphone"
                          className="edit-input"
                        />
                      </td>
                      <td>
                        <input
                          type="date"
                          value={editForm.date || ''}
                          onChange={(e) => setEditForm({...editForm, date: e.target.value})}
                          className="edit-input"
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={editForm.adresseDepart || ''}
                          onChange={(e) => setEditForm({...editForm, adresseDepart: e.target.value})}
                          placeholder="Départ"
                          className="edit-input"
                        />
                        <input
                          type="text"
                          value={editForm.adresseArrivee || ''}
                          onChange={(e) => setEditForm({...editForm, adresseArrivee: e.target.value})}
                          placeholder="Arrivée"
                          className="edit-input"
                        />
                      </td>
                      <td>
                        <select
                          value={editForm.typeService || ''}
                          onChange={(e) => setEditForm({...editForm, typeService: e.target.value})}
                          className="edit-select"
                        >
                          <option value="maison">Déménagement maison</option>
                          <option value="bureau">Déménagement bureau</option>
                          <option value="meubles">Transport de meubles</option>
                          <option value="longue-distance">Transport longue distance</option>
                        </select>
                      </td>
                      <td>
                        <select
                          value={editForm.statut || ''}
                          onChange={(e) => setEditForm({...editForm, statut: e.target.value})}
                          className={`edit-select ${editForm.statut}`}
                        >
                          <option value="en attente">En attente</option>
                          <option value="confirmé">Confirmé</option>
                          <option value="terminé">Terminé</option>
                        </select>
                      </td>
                      <td className="actions">
                        <button onClick={handleSaveEdit} className="btn-save" title="Enregistrer">
                          <FaCheck />
                        </button>
                        <button onClick={() => setEditingId(null)} className="btn-cancel" title="Annuler">
                          <FaTimes />
                        </button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="client-cell">
                        <div className="client-info-compact">
                          <strong>{rdv.nom || 'Client'}</strong>
                          <small>{rdv.email}</small>
                          <small>{rdv.telephone}</small>
                        </div>
                      </td>
                      <td className="date-cell">
                        <div className="date-info">
                          <FaCalendarAlt />
                          <span>{new Date(rdv.date).toLocaleDateString('fr-FR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          })}</span>
                        </div>
                      </td>
                      <td className="address-cell">
                        <div className="address-info">
                          <small>Départ: {rdv.adresseDepart || 'Non spécifié'}</small>
                          <small>Arrivée: {rdv.adresseArrivee || 'Non spécifié'}</small>
                        </div>
                      </td>
                      <td>
                        <span className="service-badge">{rdv.typeService}</span>
                      </td>
                      <td>
                        <select
                          value={rdv.statut}
                          onChange={(e) => handleStatutChange(rdv.id, rdv.userId, e.target.value)}
                          className={getStatutClass(rdv.statut)}
                        >
                          <option value="en attente">En attente</option>
                          <option value="confirmé">Confirmé</option>
                          <option value="terminé">Terminé</option>
                        </select>
                      </td>
                      <td className="actions">
                        <button onClick={() => viewDetails(rdv)} className="btn-view" title="Voir détails">
                          <FaEye />
                        </button>
                        <button onClick={() => handleEdit(rdv)} className="btn-edit" title="Modifier">
                          <FaEdit />
                        </button>
                        <button onClick={() => handleDelete(rdv.id, rdv.userId)} className="btn-delete" title="Supprimer">
                          <FaTrash />
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      
      {showModal && selectedRdv && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Détails du rendez-vous</h3>
              <button className="modal-close" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-section">
                <h4><FaUser /> Informations client</h4>
                <p><strong>Nom:</strong> {selectedRdv.nom}</p>
                <p><strong>Email:</strong> {selectedRdv.email}</p>
                <p><strong>Téléphone:</strong> {selectedRdv.telephone}</p>
              </div>
              <div className="detail-section">
                <h4><FaCalendarAlt /> Détails du déménagement</h4>
                <p><strong>Date:</strong> {new Date(selectedRdv.date).toLocaleDateString('fr-FR', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}</p>
                <p><strong>Service:</strong> {selectedRdv.typeService}</p>
                <p><strong>Statut:</strong> <span className={getStatutClass(selectedRdv.statut)}>{getStatutText(selectedRdv.statut)}</span></p>
              </div>
              <div className="detail-section">
                <h4><FaMapMarkerAlt /> Adresses</h4>
                <p><strong>Départ:</strong> {selectedRdv.adresseDepart || 'Non spécifié'}</p>
                <p><strong>Arrivée:</strong> {selectedRdv.adresseArrivee || 'Non spécifié'}</p>
              </div>
              {selectedRdv.message && (
                <div className="detail-section">
                  <h4>Message du client</h4>
                  <p className="message-text">{selectedRdv.message}</p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={closeModal}>Fermer</button>
              <button className="btn-primary" onClick={() => {
                handleEdit(selectedRdv);
                closeModal();
              }}>Modifier</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestionRendezVous;